import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import ConfirmationModal from "../../../../../Components/ConfirmationModal/ConfirmationModal"
import { MobergTheme } from "../../../../../Components/MobergThemes/MobergColors"

import {
	ISO_CARD_HEIGHT_MM,
	ISO_CARD_WIDTH_MM,
	KEYBOARD_STEP_MM,
	MANUAL_PRESET,
	MAX_PX_PER_MM,
	MIN_PX_PER_MM,
	SHIFT_MULTIPLIER,
} from "../../../../../Pages/Data/Visualize/DataReview/Calibration/constants"
import { MonitorPreset } from "../../../../../Pages/Data/Visualize/DataReview/Calibration/types"
import { useModalProvider } from "../../../../../Providers/ModalProvider"

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const calculatePxPerMmFromDiagonal = (diagonalInches: number, aspectRatio: number): number => {
	const diagonalMm = diagonalInches * 25.4
	const width = Math.sqrt(diagonalMm ** 2 / (1 + 1 / aspectRatio ** 2))
	const screenWidthPx = window.screen.width
	return screenWidthPx / width
}

/**
 * Makes creating confirmation modals less repetitive.
 * After the user confirms, onConfirm runs then the parent modal closes automatically.
 */
export const useConfirmation = () => {
	const { createModal, close } = useModalProvider()

	const confirm = useCallback(
		(
			title: string,
			description: string,
			confirmText: string,
			theme: MobergTheme,
			onConfirm?: () => void,
		) => {
			createModal(
				<ConfirmationModal
					title={title}
					description={description}
					confirmButton={{ text: confirmText, theme }}
					afterClose={(isCancel: boolean) => {
						// ConfirmationModal calls afterClose(true) when cancelled, afterClose() when confirmed.
						// Only run the action and close the parent modal if the user confirmed.
						if (!isCancel) {
							onConfirm?.()
							close()
						}
					}}
				/>,
			)
		},
		[createModal, close],
	)

	return { confirm, close }
}

/** Manages state for the calibration modal */
export const useCalibrationState = (savedPxPerMm: number) => {
	const [pxPerMm, setPxPerMm] = useState(savedPxPerMm)
	const [activePreset, setActivePreset] = useState<string | null>(null)
	const baselineRef = useRef(pxPerMm)

	const cardWidthPx = useMemo(() => pxPerMm * ISO_CARD_WIDTH_MM, [pxPerMm])
	const cardHeightPx = useMemo(() => pxPerMm * ISO_CARD_HEIGHT_MM, [pxPerMm])
	const hasChanges = useMemo(
		() => Math.abs(pxPerMm - baselineRef.current) > 0.00001,
		[pxPerMm],
	)

	const adjustByMm = useCallback((delta: number) => {
		setPxPerMm(prev =>
			clamp(prev * (1 + delta / ISO_CARD_WIDTH_MM), MIN_PX_PER_MM, MAX_PX_PER_MM),
		)
		setActivePreset(MANUAL_PRESET)
	}, [])

	const applyPreset = useCallback((preset: MonitorPreset) => {
		const presetPxPerMm = calculatePxPerMmFromDiagonal(
			preset.diagonalInches,
			preset.aspectRatio,
		)
		setPxPerMm(clamp(presetPxPerMm, MIN_PX_PER_MM, MAX_PX_PER_MM))
		setActivePreset(preset.label)
	}, [])

	const reset = useCallback((value: number) => {
		setPxPerMm(value)
		baselineRef.current = value
	}, [])

	const setManualMode = useCallback(() => setActivePreset(MANUAL_PRESET), [])

	return {
		pxPerMm,
		activePreset,
		cardWidthPx,
		cardHeightPx,
		hasChanges,
		adjustByMm,
		applyPreset,
		setManualMode,
		reset,
	}
}

/**
 * Captures all arrow key presses when the modal is open.
 *
 * Uses two handlers:
 * - onKeyDownCapture: handles keys when focus is inside the modal (capture phase)
 * - useEffect listener: catches keys when focus is outside the modal but modal is still open
 */
export const useArrowKeys = ({
	modalRef,
	onAdjust,
}: {
	modalRef: React.RefObject<HTMLElement>
	onAdjust: (delta: number) => void
}) => {
	/** Processes arrow key input and returns true if the key was handled */
	const processArrowKey = useCallback(
		(key: string, shiftKey: boolean) => {
			const magnitude = shiftKey ? KEYBOARD_STEP_MM * SHIFT_MULTIPLIER : KEYBOARD_STEP_MM

			if (key === "ArrowLeft" || key === "ArrowDown") {
				onAdjust(-magnitude)
				return true
			}

			if (key === "ArrowRight" || key === "ArrowUp") {
				onAdjust(magnitude)
				return true
			}

			return false
		},
		[onAdjust],
	)

	// Handle keypresses when focus is inside the modal (capture phase)
	const onKeyDownCapture = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (!e.key.startsWith("Arrow")) return
			if (processArrowKey(e.key, e.shiftKey)) {
				e.preventDefault()
				e.stopPropagation()
			}
		},
		[processArrowKey],
	)

	// Catch keys when focus is outside the modal but modal is still open
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.ctrlKey || e.metaKey || e.altKey || !e.key.startsWith("Arrow")) return

			// Let the modal's onKeyDownCapture handle its own events
			if (
				modalRef.current &&
				e.target instanceof Node &&
				modalRef.current.contains(e.target)
			) {
				return
			}

			if (processArrowKey(e.key, e.shiftKey)) {
				e.preventDefault()
				e.stopPropagation()
				e.stopImmediatePropagation()
			}
		}

		window.addEventListener("keydown", handler, true)
		return () => window.removeEventListener("keydown", handler, true)
	}, [modalRef, processArrowKey])

	return onKeyDownCapture
}
